import { useState, useCallback } from "react";
import type {
  Direction,
  DrillSelection,
  GradeAxes,
  PickerSlot,
} from "../../api/contract";
import {
  getDrillCandidates,
  gradeDrillAttempt,
  recordDrillOutcome,
  selectDrillPost,
} from "../../lib/api";
import type { DrillCandidate } from "@bindery/yominoki-db/queries/drill";
import { DrillPicker } from "./drill-picker";
import { DrillPrompt } from "./drill-prompt";
import { DrillReveal } from "./drill-reveal";
import { DrillEngagement } from "./drill-engagement";
import { Toggle } from "@bindery/ui/components/ui/toggle";

type DrillState =
  | "picking"
  | "loading"
  | "prompting"
  | "grading"
  | "grading_error"
  | "revealed"
  | "between_rounds"
  | "done";

interface RoundResult {
  direction: Direction;
  attempt: string;
  grade: GradeAxes;
}

export function DrillSession({
  initialCandidates,
}: {
  initialCandidates: PickerSlot[];
}) {
  const [state, setState] = useState<DrillState>("picking");
  const [direction, setDirection] = useState<Direction>("ja_to_en");
  const [candidates, setCandidates] = useState<PickerSlot[]>(initialCandidates);
  const [selection, setSelection] = useState<DrillSelection | null>(null);
  const [selectedCandidate, setSelectedCandidate] =
    useState<DrillCandidate | null>(null);
  // Written and never read, in yominoki and here: the picker's translation is
  // superseded by `selection.translation` the moment the card is chosen. Kept
  // as a write-only slot rather than deleted, because the writes are the
  // record of which slot was picked and the reveal surface is where a future
  // "you picked the cached one" affordance would read it.
  const [, setSelectedTranslation] = useState<string | null>(null);

  // Paired-direction tracking
  const [firstDirection, setFirstDirection] = useState<Direction>("ja_to_en");
  const [round, setRound] = useState<1 | 2>(1);
  // Also write-only today; `RoundResult` accumulates both directions' grades
  // for the paired-drill summary the "done" state does not render yet.
  const [, setRoundResults] = useState<RoundResult[]>([]);

  // Current round state
  const [currentGrade, setCurrentGrade] = useState<GradeAxes | null>(null);
  const [currentAttempt, setCurrentAttempt] = useState("");
  const [gradingError, setGradingError] = useState<string | null>(null);

  const handleDirectionToggle = useCallback(async () => {
    if (state !== "picking") return;
    const newDir = direction === "ja_to_en" ? "en_to_ja" : "ja_to_en";
    setDirection(newDir);
    setState("loading");
    const newCandidates = await getDrillCandidates(newDir);
    setCandidates(newCandidates);
    setState("picking");
  }, [direction, state]);

  const handleSelect = useCallback(
    async (slot: PickerSlot) => {
      if (!slot) return;
      setState("loading");
      setSelectedCandidate(slot.candidate);
      setSelectedTranslation(slot.translation);
      setFirstDirection(direction);
      setRound(1);
      setRoundResults([]);

      const sel = await selectDrillPost(
        slot.candidate.sentence_id,
        slot.candidate.reading_id,
        slot.candidate.text,
        slot.candidate.sentence_tokens,
      );
      setSelection(sel);
      setState("prompting");
    },
    [direction],
  );

  const handleSubmitAttempt = useCallback(
    async (attempt: string) => {
      if (!selection || !selectedCandidate) return;
      setState("grading");
      setCurrentAttempt(attempt);
      setGradingError(null);

      const currentDirection =
        round === 1 ? firstDirection : oppositeDirection(firstDirection);
      const reference =
        currentDirection === "ja_to_en"
          ? selection.translation
          : selection.sentence;

      try {
        const grade = await gradeDrillAttempt({
          sentenceId: selectedCandidate.sentence_id,
          direction: currentDirection,
          attempt,
          reference,
          focusLemmas: selection.stretchLemmas,
        });

        setCurrentGrade(grade);

        // Record fluency outcome. A failure here is non-fatal — the user
        // already has their grade, so surface a warning but still reveal.
        try {
          await recordDrillOutcome({
            sentenceId: selectedCandidate.sentence_id,
            readingId: selectedCandidate.reading_id,
            direction: currentDirection,
            stretchLemmas: selection.stretchLemmas,
            grade,
          });
        } catch (err) {
          console.error("recordDrillOutcome failed:", err);
        }

        setState("revealed");
      } catch (err) {
        console.error("gradeDrillAttempt failed:", err);
        setGradingError(
          err instanceof Error ? err.message : "Failed to grade attempt",
        );
        setState("grading_error");
      }
    },
    [selection, selectedCandidate, round, firstDirection],
  );

  const handleRetryGrading = useCallback(() => {
    setGradingError(null);
    setState("prompting");
  }, []);

  const handleContinue = useCallback(() => {
    if (round === 1) {
      // Save round 1 result, proceed to round 2
      if (currentGrade) {
        setRoundResults((prev) => [
          ...prev,
          {
            direction: firstDirection,
            attempt: currentAttempt,
            grade: currentGrade,
          },
        ]);
      }
      setRound(2);
      setCurrentGrade(null);
      setCurrentAttempt("");
      setState("prompting");
    } else {
      // Both rounds done
      if (currentGrade) {
        setRoundResults((prev) => [
          ...prev,
          {
            direction: oppositeDirection(firstDirection),
            attempt: currentAttempt,
            grade: currentGrade,
          },
        ]);
      }
      setState("done");
    }
  }, [round, firstDirection, currentGrade, currentAttempt]);

  const handleNextPost = useCallback(async () => {
    setState("loading");
    const newCandidates = await getDrillCandidates(direction);
    setCandidates(newCandidates);
    setSelection(null);
    setSelectedCandidate(null);
    setSelectedTranslation(null);
    setCurrentGrade(null);
    setCurrentAttempt("");
    setRoundResults([]);
    setRound(1);
    setState("picking");
  }, [direction]);

  const currentDirection =
    round === 1 ? firstDirection : oppositeDirection(firstDirection);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Sentence Drill</h1>
        {state === "picking" && (
          <Toggle
            pressed={direction === "en_to_ja"}
            onPressedChange={() => void handleDirectionToggle()}
            aria-label="Toggle direction"
            className="text-sm"
          >
            {direction === "ja_to_en" ? "JA → EN" : "EN → JA"}
          </Toggle>
        )}
      </header>

      {state === "loading" && (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground animate-pulse">Loading...</p>
        </div>
      )}

      {state === "picking" && (
        <DrillPicker
          slots={candidates}
          direction={direction}
          onSelect={(slot) => void handleSelect(slot)}
        />
      )}

      {state === "prompting" && selection && (
        <DrillPrompt
          direction={currentDirection}
          sentence={selection.sentence}
          translation={selection.translation}
          glosses={selection.glosses}
          stretchLemmas={selection.stretchLemmas}
          round={round}
          initialAttempt={currentAttempt}
          onSubmit={(attempt) => void handleSubmitAttempt(attempt)}
        />
      )}

      {state === "grading" && (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground animate-pulse">Grading...</p>
        </div>
      )}

      {state === "grading_error" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <p className="text-destructive text-sm">
            Grading failed{gradingError ? `: ${gradingError}` : "."}
          </p>
          <button
            onClick={handleRetryGrading}
            className="hover:bg-muted rounded-md border px-4 py-2 text-sm"
          >
            Try again
          </button>
        </div>
      )}

      {state === "revealed" &&
        selection &&
        currentGrade &&
        selectedCandidate && (
          <>
            <DrillReveal
              direction={currentDirection}
              sentence={selection.sentence}
              translation={selection.translation}
              attempt={currentAttempt}
              grade={currentGrade}
              round={round}
              onContinue={handleContinue}
            />
            <DrillEngagement
              readingId={selectedCandidate.reading_id}
              sourceUri={selectedCandidate.post_uri || null}
              sourceCid={selectedCandidate.source_cid}
            />
          </>
        )}

      {state === "done" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <h2 className="text-lg font-medium">Drill complete</h2>
          <p className="text-muted-foreground text-sm">
            You drilled both directions for this sentence.
          </p>
          <button
            onClick={() => void handleNextPost()}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm"
          >
            Next post
          </button>
        </div>
      )}
    </div>
  );
}

function oppositeDirection(d: Direction): Direction {
  return d === "ja_to_en" ? "en_to_ja" : "ja_to_en";
}
